import { parse } from 'date-fns';
import { useStore } from 'framework7-react';
import { useEffect, useState } from 'react';
import { CoatingStep1FormData } from './useCoatingStep1';
import { CoatingStep2FormData } from './useCoatingStep2';
import { CoatingStep3FormData } from './useCoatingStep3';
import { store } from '@/config/store';
import {
  coatingBodySizeOptions,
  coatingPolishOptionOptions,
} from '@/consts/coating';
import {
  CoatingCourseItem,
  CoatingOptionItem,
  CreateCoatingParams,
} from '@/types/api/coatingApi';
import { Car } from '@/types/api/customerApi';
import { formatDate } from '@/utils/date';

export interface CoatingDisplayDetail {
  courseName?: string;
  bodySizeLabel?: string;
  polishOptionLabel?: string;
  serviceContents?: string;
  amount?: string;
  datetime1?: string;
  datetime2?: string;
  datetime3?: string;
  name?: string;
  nameKana?: string;
  phoneNo?: string;
  carLabel?: string;
  makerName?: string;
  carName?: string;
  bodyColor?: string;
  pastCoatingHistory?: string;
  unitMemo?: string;
  cancellationPercent?: number;
}

export const useCoatingStep4 = (
  step1Data: CoatingStep1FormData,
  step2Data: CoatingStep2FormData,
  step3Data: CoatingStep3FormData,
  courseItems: CoatingCourseItem[],
  optionItems: CoatingOptionItem[],
  totalFee: number,
  customerEmail?: string,
) => {
  const carsInfo = useStore(store, 'carsInfo') as Required<Car>[];
  const [data, setData] = useState<CreateCoatingParams>();
  const [display, setDisplay] = useState<CoatingDisplayDetail>({});

  useEffect(() => {
    if (!step1Data.coatingItemCode || !step2Data.mUnitId || !step3Data) return;

    const {
      tStockCarId = 0,
      bodySize,
      polishOption,
      coatingItemCode = 0,
      optionItemCodes = [],
      bodyColor = '',
      pastCoatingHistory = '',
    } = step1Data;
    const {
      preferredDatetimes: { datetime1 = '', datetime2 = '', datetime3 = '' },
      reservationHolder: {
        familyName = '',
        familyNameKana = '',
        firstName = '',
        firstNameKana = '',
        tel1 = '',
        tel2 = '',
        tel3 = '',
      },
      mUnitId = 0,
      unitMemo = '',
    } = step2Data;

    const course = courseItems.find((c) => c.code === coatingItemCode);
    const selectedOptions = optionItems.filter((i) =>
      optionItemCodes.includes(i.code),
    );

    const carItem = carsInfo?.find((c) => c.t_stock_car_id === tStockCarId);

    const bodySizeLabel =
      coatingBodySizeOptions.find((o) => o.value === bodySize)?.label || '';
    const polishOptionLabel =
      coatingPolishOptionOptions.find((o) => o.value === polishOption)?.label ||
      '';

    const optionNames = selectedOptions.map((i) => i.name).join('、');

    setDisplay({
      courseName: course?.name || '',
      bodySizeLabel,
      polishOptionLabel,
      serviceContents: optionNames
        ? `${course?.name || ''}、${optionNames}`
        : course?.name || '',
      amount: totalFee.toLocaleString(),
      datetime1: datetime1
        ? `${formatDate(parse(datetime1, 'yyyy-MM-dd HH:mm:ss', new Date()))}～`
        : '',
      datetime2: datetime2
        ? `${formatDate(parse(datetime2, 'yyyy-MM-dd HH:mm:ss', new Date()))}～`
        : '',
      datetime3: datetime3
        ? `${formatDate(parse(datetime3, 'yyyy-MM-dd HH:mm:ss', new Date()))}～`
        : '',
      name: `${familyName} ${firstName}`,
      nameKana: `${familyNameKana} ${firstNameKana}`,
      phoneNo: `${tel1}-${tel2}-${tel3}`,
      carLabel: carItem
        ? `${carItem.transport_name}${carItem.class_no}${carItem.hiragana}${carItem.registry_no}`
        : '',
      makerName: carItem?.maker_nm || '',
      carName: carItem?.car_nm || '',
      bodyColor,
      pastCoatingHistory,
      unitMemo,
      cancellationPercent: course?.cancellation_charge?.charge_percent,
    });

    // API送信用パラメータ（t_payment_managements_id は決済完了後に設定）
    setData({
      m_customer_id: store.state.authInfo.m_customer_id,
      m_affiliate_id: store.state.customerInfo.m_affiliate_id,
      t_payment_managements_id: 0, // 決済完了後に上書き
      service_type: 'coating',
      body_size: bodySize!,
      polish_option: polishOption!,
      coating_item_code: coatingItemCode,
      t_stock_car_id: tStockCarId,
      m_unit_id: mUnitId,
      option_item_codes: optionItemCodes.length ? optionItemCodes : null,
      preferred_datetimes: {
        datetime1,
        datetime2,
        datetime3,
      },
      reservation_holder: {
        family_name: familyName,
        first_name: firstName,
        family_name_kana: familyNameKana,
        first_name_kana: firstNameKana,
        phone_no: `${tel1}-${tel2}-${tel3}`,
      },
      contact_method: (step2Data.contactMethod ?? 1) as 1 | 2,
      // メールアドレスは Step2 で常時必須化したため contact_method に関わらず送る
      // 顧客自身が入力した値（step2Data.contactEmail）を優先する
      contact_email: step2Data.contactEmail || customerEmail || null,
      body_color: bodyColor || undefined,
      past_coating_history: pastCoatingHistory || undefined,
      unit_memo: unitMemo || undefined,
      scratch_claim_consent: 1,
      extra_charge_consent: 1,
    });
  }, [
    carsInfo,
    courseItems,
    optionItems,
    step1Data,
    step2Data,
    step3Data,
    totalFee,
    customerEmail,
  ]);

  return {
    display,
    data,
  };
};
